import { Controller } from "@hotwired/stimulus";
import { type FrameElement, watchFrame } from "../dice/frame_feed";
import { feedRollData, type RollData, rollVerdict } from "../dice/feed";
import { createRecentRolls, rollKey } from "../dice/landing";
import type { Roll } from "../dice/pool";
import { createStage, findTemplates, type Stage } from "../dice/stage";
import type { MapRollsArrived } from "../islands/map_rolls";
import type DiceRoller from "./dice_roller_controller";
import type { FailureReason } from "./dice_roller_controller";
import type MapBoard from "./map_board_controller";
import { closeOtherPanels } from "./map_panels";

const TOAST_MS = 6000;

// A monster's roll is named in the feed for the monster ("Goblin", "?????" when masked), which its row's card cannot know: its
// broadcast is found by the roll alone.
const monsterKey = ({ notation, result }: Pick<Roll, "notation" | "result">) => rollKey({ name: "", notation, result });

// What map_scenes/_board.html.erb passes from the locale.
type Labels = { secret: string; local: string; rejected: string; failed: Record<FailureReason, string> };

// Connects to data-controller="map-dice", beside map-board on the board root. The campaign's rolls come from the `map_rolls`
// island (`islands/map_rolls.tsx`), the page's one feed client: each roll it hears live plays once, on a card in the stack, or,
// while the panel shows the list, as thrown dice beside the row the list draws itself. On the board the dice-roller floats
// apart from the panel, and the board's keys reach it through here. The public table link has no account to read the island's
// JSON with, and plays from its hidden feed frame instead (`dice/frame_feed.ts`).
export default class extends Controller {
	static targets = ["panel", "toggle", "frame", "roller", "cards", "layer", "announcer", "toasts", "toastTemplate"];
	static values = { labels: Object, color: String };
	declare readonly panelTarget: HTMLElement;
	declare readonly hasPanelTarget: boolean;
	declare readonly toggleTarget: HTMLElement;
	declare readonly frameTarget: FrameElement;
	declare readonly hasFrameTarget: boolean;
	declare readonly rollerTarget: HTMLElement;
	declare readonly hasRollerTarget: boolean;
	declare readonly cardsTarget: HTMLElement;
	declare readonly layerTarget: HTMLElement;
	declare readonly hasLayerTarget: boolean;
	declare readonly announcerTarget: HTMLElement;
	declare readonly toastsTarget: HTMLElement;
	declare readonly hasToastsTarget: boolean;
	declare readonly toastTemplateTarget: HTMLTemplateElement;
	declare readonly labelsValue: Labels;
	declare readonly colorValue: string;
	frameWatch?: ReturnType<typeof watchFrame>;
	stage: Stage;
	recent = createRecentRolls();

	connect() {
		this.stage = createStage({
			cards: this.cardsTarget,
			layer: this.hasLayerTarget ? this.layerTarget : null,
			announcer: this.announcerTarget,
			...findTemplates(this.element),
			roller: () => (this.hasRollerTarget ? this.rollerTarget : null),
			alwaysThrow: () => Boolean(this.board?.tableValue),
			fadeCards: () => Boolean(this.board?.tableValue),
			insets: () => (this.board ? (this.board.tableValue ? null : this.board.chromeInsets()) : this.panelInsets()),
			refocus: () => this.roller?.focus(),
		});
		if (this.hasFrameTarget) this.frameWatch = watchFrame(this.frameTarget, { live: () => !this.board?.dropped, play: (data) => this.play(data, false) });
	}

	disconnect() {
		this.stop();
	}

	// The permanent root keeps its cards and dice when Turbo carries it to the next page; a table that lost its access stops too.
	stop() {
		this.frameWatch?.stop();
		this.frameWatch = undefined;
		this.stage.clear();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	// The roller's own roll plays at the click, and its broadcast then plays
	// nothing. With the panel showing the list its entry plays when it arrives, as anyone's. A sheet's chip in its panel rolls the
	// same way, naming its character and what it rolled. A monster's roll (`monster`) only reserves its broadcast here: its card
	// plays when the server has said whether the feed masks it (`posted`), which also reserves it for a list toggled mid-roll.
	rolling({ detail: { roll, name, description, monster } }: CustomEvent<{ roll: Roll; name: string; description?: string; monster?: boolean }>) {
		if (this.listShown) return;
		if (monster) {
			this.recent.remember(monsterKey(roll));
			return;
		}
		const data = this.ownRoll(roll, name, description ?? null);
		const key = rollKey(data);
		this.recent.remember(key);
		this.stage.show(data, { key, own: true });
	}

	posted({
		detail: { roll, name, description, note, noteIcon },
	}: CustomEvent<{ roll: Roll; name: string; description?: string; note?: string; noteIcon?: "masks" }>) {
		if (this.listShown) return;
		const data = { ...this.ownRoll(roll, name, description ?? null), ...(note ? { note, noteIcon } : {}) };
		this.recent.consume(monsterKey(roll));
		this.recent.remember(monsterKey(roll));
		this.stage.show(data, { key: rollKey(data), own: true });
	}

	// A secret roll never reaches the feed, so its card shows even with the panel open.
	secret({ detail: { roll, name } }: CustomEvent<{ roll: Roll; name: string }>) {
		const data = this.ownRoll(roll, name, this.labelsValue.secret);
		this.stage.show(data, { key: rollKey(data), own: true });
	}

	// A roll too large for the log is never posted either, so it shows even with the panel open, with the note that only its roller sees it.
	local({ detail: { roll, name, description } }: CustomEvent<{ roll: Roll; name: string; description?: string }>) {
		const data = { ...this.ownRoll(roll, name, description ?? null), note: this.labelsValue.local };
		this.stage.show(data, { key: rollKey(data), own: true });
	}

	// A roll the server refused never lands, so its card goes; one that may
	// have landed stays, and its broadcast still finds the key.
	// A monster's roll plays its card only on `posted`, so offline shows it here, once, and its reservation keeps a landed broadcast quiet.
	failed({
		detail: { reason, roll, name, description, monster },
	}: CustomEvent<{ reason: FailureReason; roll: Roll; name: string; description?: string; monster?: boolean }>) {
		this.toast(this.labelsValue.failed[reason]);
		if (reason === "offline") {
			if (!monster || this.listShown) return;
			const data = this.ownRoll(roll, name, description ?? null);
			this.recent.consume(monsterKey(roll));
			this.recent.remember(monsterKey(roll));
			this.stage.show(data, { key: rollKey(data), own: true });
			return;
		}
		const key = rollKey({ name, notation: roll.notation, result: roll.result });
		this.recent.forget(key);
		this.recent.forget(monsterKey(roll));
		this.stage.withdraw(key);
	}

	// The waiting view has no board to toast on and keeps its own stack (index.html.erb).
	toast(message: string) {
		if (this.board) return this.board.toast(message);
		if (!this.hasToastsTarget) return;
		const toast = this.toastTemplateTarget.content.firstElementChild.cloneNode(true) as HTMLElement;
		toast.querySelector("[data-toast-message]").textContent = message;
		this.toastsTarget.append(toast);
		setTimeout(() => toast.remove(), TOAST_MS);
	}

	ownRoll(roll: Roll, name: string, description: string | null): RollData {
		return { name, notation: roll.notation, result: roll.result, description, verdict: rollVerdict(roll), color: this.ownColor };
	}

	get ownColor() {
		const board = this.board;
		return (board?.state && board.participant(board.accountIdValue)?.color) || this.colorValue || null;
	}

	// A roll pushed live plays unless the tab is hidden or the board dropped: those are history, and a socket that outlived the drop
	// can deliver one. A roll the feed only found by refetching, after a reconnect or a tab's return, was made while the board was
	// away, and never plays.
	arrived({ detail: { roll, own, live } }: CustomEvent<MapRollsArrived>) {
		if (!live || document.hidden || this.board?.dropped) return;
		if ((roll.kind === "monster" || roll.kind === "masked") && this.recent.consume(monsterKey(roll))) return;
		this.play(feedRollData(roll), own);
	}

	play(data: RollData, own: boolean) {
		const key = rollKey(data);
		if (this.recent.consume(key)) return;
		// Another player's roll is a row in the campaign's list, never a card, but on the table view, which shows them all.
		if (this.listShown || (!own && !this.board?.tableValue)) this.stage.throwOnly(data, { key, own });
		else this.stage.show(data, { key, own });
	}

	rejected() {
		this.toast(this.labelsValue.rejected);
	}

	reload() {
		this.frameWatch?.reload();
	}

	get panelShown() {
		return this.hasPanelTarget && !this.panelTarget.hidden;
	}

	// The panel is open on loaded rows (the island's `data-listed`), where a loading or failed list would play nothing.
	get listShown() {
		return this.panelShown && Boolean(this.panelTarget.querySelector("[data-listed]"));
	}

	// The waiting screen has no board to measure its chrome: the open panel, docked right or a bottom sheet, is all there is.
	panelInsets() {
		if (!this.panelShown) return { right: 0, bottom: 0 };
		const page = this.element.getBoundingClientRect();
		const panel = this.panelTarget.getBoundingClientRect();
		return panel.width > page.width * 0.9 ? { right: 0, bottom: page.bottom - panel.top } : { right: page.right - panel.left, bottom: 0 };
	}

	toggle() {
		if (this.panelTarget.hidden) this.show();
		else this.close();
	}

	show() {
		closeOtherPanels(this.application, this.element, "map-dice");
		this.panelTarget.hidden = false;
		this.toggleTarget.setAttribute("aria-pressed", "true");
	}

	close() {
		if (!this.hasPanelTarget || this.panelTarget.hidden) return;
		// Read before hiding: a hidden element gives its focus up at once.
		const focused = this.panelTarget.contains(document.activeElement);
		this.panelTarget.hidden = true;
		this.toggleTarget.setAttribute("aria-pressed", "false");
		if (focused) this.toggleTarget.focus({ preventScroll: true });
	}

	get roller() {
		if (!this.hasRollerTarget) return null;
		return this.application.getControllerForElementAndIdentifier(this.rollerTarget, "dice-roller") as DiceRoller | null;
	}

	// The list's empty state asks for the roller. The panel closes first: on a phone it would cover the dice box.
	openRoller() {
		this.close();
		if (this.roller && !this.roller.opened) this.roller.open();
		this.roller?.focus();
	}

	toggleRoller() {
		this.roller?.toggle();
		return Boolean(this.roller);
	}

	closeRoller() {
		if (!this.roller?.opened) return false;
		this.roller.close();
		return true;
	}
}
